import React, { useEffect, useState } from "react";
import { Download, CheckCircle2, Clock } from "lucide-react";
import { api, API } from "../../lib/api";

export const AdminSalesTab = () => {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [filters, setFilters] = useState({ status: "", date_from: "", date_to: "" });
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filters.status) params.status = filters.status;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const [s, o] = await Promise.all([
        api.get("/admin/sales/summary").then((x) => x.data),
        api.get("/admin/orders", { params }).then((x) => x.data),
      ]);
      setSummary(s);
      setOrders(o);
    } catch {
      // ignore
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []); // eslint-disable-line
  useEffect(() => { load(); }, [filters.status, filters.date_from, filters.date_to]); // eslint-disable-line

  const markPaid = async (n) => {
    if (!window.confirm(`Mark ${n} as PAID (this credits artist ledgers)?`)) return;
    await api.patch(`/admin/orders/${n}`, { status: "paid" });
    load();
  };

  const setStatus = async (n, status) => {
    let payload = { status };
    if (status === "shipped") {
      const tracking = window.prompt(
        "Enter tracking number (CTT, DHL, etc.) — leave blank if you don't have one yet:",
        ""
      );
      if (tracking === null) return; // user cancelled
      const trimmed = tracking.trim();
      if (trimmed) payload.tracking_number = trimmed;
      const eta = window.prompt(
        "Estimated delivery (working days from today) — e.g. 5. Leave blank to skip the customer-facing countdown.",
        "5"
      );
      if (eta && !isNaN(Number(eta))) payload.delivery_eta_days = Number(eta);
    }
    await api.patch(`/admin/orders/${n}`, payload);
    load();
  };

  const editTracking = async (n, current) => {
    const next = window.prompt("Update tracking number:", current || "");
    if (next === null) return;
    await api.patch(`/admin/orders/${n}`, { status: "shipped", tracking_number: next.trim() });
    load();
  };

  const setShipEta = async (n, current) => {
    const v = window.prompt(
      "Working days from now until you'll ship this order (1-30). Leave blank to clear.",
      current ?? "3"
    );
    if (v === null) return;
    await api.patch(`/admin/orders/${n}`, {
      status: "paid",
      ship_eta_days: v === "" ? null : Number(v),
    });
    load();
  };

  const refund = async (n) => {
    if (!window.confirm(`REFUND order ${n}?\n\nThis will reverse the artist ledger credits and free up the discount code use. The order will be marked refunded.`)) return;
    try {
      await api.post(`/admin/orders/${n}/refund`);
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Refund failed");
    }
  };

  const exportCsv = () => {
    const token = localStorage.getItem("kult_token");
    fetch(`${API}/admin/sales/export`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "kultopia-sales.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <section data-testid="admin-sales-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <p className="font-mono text-xs tracking-[0.3em] text-kult-yellow">/ SALES & ORDERS</p>
        <button onClick={exportCsv} className="inline-flex items-center gap-2 border border-kult-border px-4 py-2 font-mono text-xs tracking-[0.25em] hover:border-kult-pink" data-testid="sales-export">
          <Download size={14} /> EXPORT CSV
        </button>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Stat label="REVENUE" value={`€${summary.total_revenue.toFixed(0)}`} accent testId="sales-stat-revenue" />
          <Stat label="ORDERS PAID" value={summary.orders_paid} />
          <Stat label="UNITS" value={summary.units_sold} />
          <Stat label="TICKETS" value={summary.tickets_sold} />
          <Stat label="TICKET REV." value={`€${summary.tickets_revenue.toFixed(0)}`} />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <label className="block">
          <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">STATUS</span>
          <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="w-full bg-kult-black border border-kult-border px-3 py-2 font-mono text-sm"
            data-testid="sales-filter-status">
            <option value="">ALL</option>
            <option value="awaiting_payment">AWAITING PAYMENT</option>
            <option value="paid">PAID</option>
            <option value="shipped">SHIPPED</option>
            <option value="cancelled">CANCELLED</option>
            <option value="refunded">REFUNDED</option>
          </select>
        </label>
        <label className="block">
          <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">FROM</span>
          <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="w-full bg-transparent border border-kult-border px-3 py-2 font-mono text-sm" data-testid="sales-filter-from" />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] tracking-[0.3em] text-kult-muted block mb-1">TO</span>
          <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="w-full bg-transparent border border-kult-border px-3 py-2 font-mono text-sm" data-testid="sales-filter-to" />
        </label>
      </div>

      {summary?.top_products?.length > 0 && (
        <div className="border border-kult-border p-4 mb-6">
          <p className="font-mono text-[10px] tracking-[0.3em] text-kult-yellow mb-3">TOP PRODUCTS</p>
          <div className="space-y-1">
            {summary.top_products.map((p) => (
              <div key={p.name} className="flex justify-between items-center text-sm" data-testid={`sales-top-${p.name}`}>
                <span className="font-mono text-xs truncate flex-1">{p.name}</span>
                <span className="font-mono text-xs text-kult-muted px-3">×{p.units}</span>
                <span className="font-mono text-xs text-kult-yellow">€{p.gross.toFixed(0)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="border border-kult-border" data-testid="admin-orders-list">
        <div className="grid grid-cols-12 gap-2 px-4 py-3 bg-kult-black2 border-b border-kult-border font-mono text-[10px] tracking-[0.3em] text-kult-muted">
          <span className="col-span-3">ORDER</span>
          <span className="col-span-3">CUSTOMER</span>
          <span className="col-span-2">DATE</span>
          <span className="col-span-1 text-right">TOTAL</span>
          <span className="col-span-1">STATUS</span>
          <span className="col-span-2 text-right">ACTION</span>
        </div>
        {loading ? (
          <p className="px-4 py-6 font-mono text-xs text-kult-muted">LOADING...</p>
        ) : orders.length === 0 ? (
          <p className="px-4 py-6 font-mono text-xs text-kult-muted">No orders match.</p>
        ) : orders.map((o) => {
          const isPickup = o.shipping_method && o.shipping_method !== "courier";
          const addressLine = isPickup
            ? (o.shipping_method === "pickup_roster"
                ? `🤝 PICK-UP WITH ${(o.pickup_artist_name || "ROSTER").toUpperCase()}`
                : `🎟 PICK-UP AT ${(o.pickup_event_title || "EVENT").toUpperCase()}`)
            : [o.shipping_address, o.apartment, o.postal_code, o.city, o.country]
                .filter(Boolean)
                .join(", ");
          return (
          <div key={o.order_number} className="grid grid-cols-12 gap-2 px-4 py-3 border-b border-kult-border last:border-b-0 items-start" data-testid={`admin-order-${o.order_number}`}>
            <div className="col-span-3 min-w-0">
              <p className="font-mono text-[11px] truncate text-white">{o.order_number}</p>
              {o.language && (
                <p className="font-mono text-[9px] tracking-[0.25em] text-kult-yellow mt-0.5">
                  LANG · {String(o.language).toUpperCase()}
                </p>
              )}
            </div>
            <div className="col-span-3 min-w-0">
              <p className="font-display text-sm leading-tight text-white truncate" data-testid={`order-name-${o.order_number}`}>
                {o.customer_name || "—"}
              </p>
              <p className="font-mono text-[10px] text-kult-muted truncate" data-testid={`order-email-${o.order_number}`}>
                {o.customer_email}
              </p>
              {o.customer_phone && (
                <p className="font-mono text-[10px] text-kult-yellow truncate" data-testid={`order-phone-${o.order_number}`}>
                  ☎ {o.customer_phone}
                </p>
              )}
              {addressLine && (
                <p className="font-mono text-[10px] text-kult-muted truncate mt-0.5" data-testid={`order-address-${o.order_number}`} title={addressLine}>
                  {addressLine}
                </p>
              )}
              {o.tracking_number && (
                <p className="font-mono text-[10px] text-kult-pink truncate mt-0.5" data-testid={`order-tracking-${o.order_number}`}>
                  ⇒ {o.tracking_number}
                </p>
              )}
              {(o.ship_eta_days != null || o.delivery_eta_days != null) && (
                <p className="font-mono text-[10px] text-kult-yellow mt-0.5" data-testid={`order-eta-${o.order_number}`}>
                  {o.ship_eta_days != null && `SHIP ${o.ship_eta_days}d`}
                  {o.ship_eta_days != null && o.delivery_eta_days != null && " · "}
                  {o.delivery_eta_days != null && `DELIVER ${o.delivery_eta_days}d`}
                </p>
              )}
            </div>
            <span className="col-span-2 font-mono text-[11px] text-kult-muted">{new Date(o.created_at).toLocaleDateString()}</span>
            <span className="col-span-1 text-right font-mono text-sm">
              €{o.total?.toFixed(2)}
              {o.discount_code && <span className="block text-[9px] text-kult-yellow">-{o.discount_code}</span>}
            </span>
            <span className="col-span-1 inline-flex items-center gap-1 font-mono text-[10px] tracking-[0.25em] uppercase">
              {o.status === "paid" ? <CheckCircle2 size={12} className="text-kult-yellow" /> : <Clock size={12} className="text-kult-muted" />}
              <span className="truncate">{o.status}</span>
            </span>
            <div className="col-span-2 flex justify-end gap-1 flex-wrap">
              {o.status !== "paid" && o.status !== "refunded" && o.status !== "shipped" && (
                <button onClick={() => markPaid(o.order_number)} className="bg-kult-yellow text-black px-2 py-1 font-mono text-[10px] tracking-[0.25em]" data-testid={`order-mark-paid-${o.order_number}`}>MARK PAID</button>
              )}
              {o.status === "paid" && (
                <>
                  <button onClick={() => setShipEta(o.order_number, o.ship_eta_days)} className="border border-kult-yellow text-kult-yellow px-2 py-1 font-mono text-[10px] tracking-[0.25em]" data-testid={`order-set-eta-${o.order_number}`}>SET ETA</button>
                  <button onClick={() => setStatus(o.order_number, "shipped")} className="bg-kult-pink text-black px-2 py-1 font-mono text-[10px] tracking-[0.25em]" data-testid={`order-ship-${o.order_number}`}>SHIP</button>
                </>
              )}
              {o.status === "shipped" && (
                <button onClick={() => editTracking(o.order_number, o.tracking_number)} className="border border-kult-pink text-kult-pink px-2 py-1 font-mono text-[10px] tracking-[0.25em]" data-testid={`order-edit-tracking-${o.order_number}`}>EDIT TRACK</button>
              )}
              {(o.status === "paid" || o.status === "shipped") && (
                <button onClick={() => refund(o.order_number)} className="border border-red-500 text-red-400 px-2 py-1 font-mono text-[10px] tracking-[0.25em] hover:bg-red-500/10" data-testid={`order-refund-${o.order_number}`}>REFUND</button>
              )}
              {o.status !== "cancelled" && o.status !== "refunded" && o.status !== "paid" && o.status !== "shipped" && (
                <button onClick={() => setStatus(o.order_number, "cancelled")} className="border border-kult-border px-2 py-1 font-mono text-[10px] tracking-[0.25em]" data-testid={`order-cancel-${o.order_number}`}>CANCEL</button>
              )}
            </div>
          </div>
        );})}
      </div>
    </section>
  );
};

function Stat({ label, value, accent, testId }) {
  return (
    <div className={`border-2 ${accent ? "border-kult-pink bg-kult-black2" : "border-kult-border"} p-4`} data-testid={testId}>
      <p className="font-mono text-[10px] tracking-[0.3em] text-kult-muted">{label}</p>
      <p className={`font-display text-2xl mt-1 ${accent ? "text-kult-pink" : "text-white"}`}>{value}</p>
    </div>
  );
}
